import { motion } from 'framer-motion';
import { Inbox, Workflow, MessageSquare, Eye, Activity } from 'lucide-react';

const solutions = [
  {
    id: 'intake',
    icon: Inbox,
    title: 'AI Intake Systems',
    description: 'Structured intake and triage for incoming requests, forms, messages, and operational inputs. Automatic extraction, categorization, and routing.',
    primary: true,
  },
  {
    id: 'automation',
    icon: Workflow,
    title: 'Workflow Automation',
    description: 'Automation layers that reduce repetitive processing, improve routing logic, and support consistent internal workflows.',
    primary: true,
  },
  {
    id: 'communication',
    icon: MessageSquare,
    title: 'Communication Automation',
    description: 'Smart interfaces for websites and messaging channels that guide users, collect structured information, and reduce manual response load.',
    primary: false,
  },
  {
    id: 'supervisory',
    icon: Eye,
    title: 'Supervisory Control',
    description: 'Operational oversight layers for monitoring, alerting, escalation paths, and system-level visibility.',
    primary: false,
  },
];

// Secondary capability - kept minimal
const secondaryCapability = {
  id: 'infrastructure',
  icon: Activity,
  title: 'Infrastructure Automation',
  description: 'For select clients: supervisory control systems for trading infrastructure and exchange environments.',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export const SolutionAreas = () => {
  return (
    <section
      id="solutions"
      data-testid="solution-areas-section"
      className="bg-white py-24 md:py-32 border-t border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-4">
            Capabilities
          </p>
          <h2
            data-testid="solutions-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 font-['Manrope']"
          >
            Core solution areas
          </h2>
        </motion.div>

        {/* Primary Solutions Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
        >
          {solutions.map((solution) => (
            <motion.div
              key={solution.id}
              variants={itemVariants}
              data-testid={`solution-card-${solution.id}`}
              className="group"
            >
              <div className={`h-full p-8 md:p-10 border rounded-sm transition-colors ${
                solution.primary 
                  ? 'bg-slate-900 border-slate-800 hover:border-slate-700' 
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}>
                <div className={`p-3 rounded-sm w-fit mb-6 ${
                  solution.primary ? 'bg-slate-800' : 'bg-white border border-slate-200'
                }`}>
                  <solution.icon 
                    className={solution.primary ? 'text-slate-300' : 'text-slate-700'} 
                    size={22} 
                    strokeWidth={1.5} 
                  />
                </div>
                <h3 className={`text-xl md:text-2xl font-semibold mb-4 font-['Manrope'] ${
                  solution.primary ? 'text-white' : 'text-slate-900'
                }`}>
                  {solution.title}
                </h3>
                <p className={`text-sm md:text-base leading-relaxed ${
                  solution.primary ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  {solution.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Secondary capability - minimal presentation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-12 pt-8 border-t border-slate-200"
        >
          <div className="flex items-start gap-6" data-testid="solution-card-infrastructure">
            <div className="p-2.5 bg-slate-100 rounded-sm flex-shrink-0">
              <Activity className="text-slate-500" size={18} strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-slate-400 font-medium mb-1">
                Advanced Capability
              </p>
              <h3 className="text-base font-semibold text-slate-700 mb-1 font-['Manrope']">
                {secondaryCapability.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed max-w-2xl">
                {secondaryCapability.description}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
